import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertCircle, ChevronLeft, ChevronRight, Download, Inbox } from 'lucide-react';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import { BillFilterBar } from '../common/BillFilterBar';
import { useMissingBillsData } from '../../hooks/useMissingBillsData';
import type { BoardRow } from '../../lib/boardRows';
import type { BillFilters } from '../../lib/billFilters';
import { lastCompletedMonth, monthLabel } from '../../lib/billMonths';
import { WORKFLOW_LABELS } from '../../lib/billWorkflow';
import { downloadCsv, toCsv } from '../../lib/csvText';
import { exportMultiSheetXLSX } from '../../utils/exportUtils';
import {
  MISSING_CSV_HEADERS, MISSING_TAGS, MISSING_TAG_LABELS, buildMissingGrid, bulkTagTargets, filterSites, gridBillOf,
  missingCsvRows, nmiMonthOptions, resolveNmiRange, siteOptions, visibleRows,
  type MissingCell, type MissingTag, type TagCellRef,
} from '../../lib/missingBills';
import { missingBillService } from '../../services/missingBillService';
import { NewBadge } from '../NewBadge';
import { MissingTagEditor } from './MissingTagEditor';

// The By NMI view of the Bulk Bill Run board (spec 2026-10-20): one row per
// enrolled NMI, one cell per month. The board has already loaded the bills, so
// they come in as props; the grid itself is lib/missingBills.

const PAGE_SIZE = 50;
const HIDDEN_FILTERS = ['verdict', 'status', 'send'] as const;
/** Clear filters here resets only what this view shows; the hidden verdict,
 *  status and send stay in the URL for the By bill view. */
const CLEAR_VISIBLE_FILTERS: Partial<BillFilters> = {
  q: '', from: '', to: '', entity: '', retailer: '', distributor: '', state: '', tariff: '',
};

export type MissingBillsByNmiProps = {
  boardRows: BoardRow[];
  boardLoading: boolean;
  boardError: string | null;
  reloadKey: number;
  filters: BillFilters;
  onFilters: (patch: Partial<BillFilters>) => void;
};

function cellText(cell: MissingCell): string {
  switch (cell.kind) {
    case 'billed': return `${WORKFLOW_LABELS[cell.status]}${cell.extra > 0 ? ` +${cell.extra}` : ''}`;
    case 'missing_invoice': return `Missing Invoice${cell.extra > 0 ? ` +${cell.extra}` : ''}`;
    case 'received_unprocessed': return `Received, not processed${cell.extra > 0 ? ` +${cell.extra}` : ''}`;
    case 'no_bill': return 'No bill';
    default: return '';
  }
}

type BulkPending = { cells: TagCellRef[]; overwrites: number; tag: MissingTag };

const plural = (n: number) => (n === 1 ? '' : 's');
const cellKey = (siteId: string, month: string) => `${siteId}|${month}`;

/** `notExpected`: tagged Not expected, so shown greyed, never in red. */
const CellBody = ({ cell, notExpected }: { cell: MissingCell; notExpected: boolean }) => {
  const text = cellText(cell);
  const location = useLocation();
  if (cell.kind === 'not_enrolled') return <span className="sr-only">Not enrolled</span>;
  const red = !notExpected && (cell.kind === 'missing_invoice' || cell.kind === 'no_bill');
  if (cell.kind === 'billed' || cell.kind === 'missing_invoice' || cell.kind === 'received_unprocessed') {
    return (
      <Link
        to={`/bill-validation/bills/${cell.billId}`}
        state={{ from: { label: 'Bulk Bill Run', to: `${location.pathname}${location.search}` } }}
        className={cn('hover:underline', red ? 'text-red-700' : notExpected ? 'text-gray-400' : 'text-gray-700')}
      >
        {text}
      </Link>
    );
  }
  return <span className={red ? 'text-red-700 font-medium' : 'text-gray-400'}>{text}</span>;
};

export const MissingBillsByNmi = ({
  boardRows, boardLoading, boardError, reloadKey, filters, onFilters,
}: MissingBillsByNmiProps) => {
  const lastCompleted = useMemo(() => lastCompletedMonth(), []);
  const range = useMemo(
    () => resolveNmiRange(filters.from, filters.to, lastCompleted),
    [filters.from, filters.to, lastCompleted],
  );
  const { sites, tags, unmatched, loading, error, tagsError, reloadTags } = useMissingBillsData(range.from, range.to, reloadKey);
  const [onlyGaps, setOnlyGaps] = useState(true);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<TagCellRef | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [bulkTag, setBulkTag] = useState<MissingTag>('chasing_retailer');
  const [bulkNote, setBulkNote] = useState('');
  const [pending, setPending] = useState<BulkPending | null>(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);
  const busy = bulkSaving || editorSaving;
  const [bulkError, setBulkError] = useState<string | null>(null);
  // Each missing cell's Tag button, so closing the editor can return focus to it.
  const tagButtons = useRef(new Map<string, HTMLButtonElement>());
  const [focusReturn, setFocusReturn] = useState<string | null>(null);
  useEffect(() => {
    // Wait until nothing is saving: the button is disabled while a save runs.
    if (!focusReturn || busy) return;
    tagButtons.current.get(focusReturn)?.focus();
    setFocusReturn(null);
  }, [focusReturn, busy]);

  const options = useMemo(
    () => ({ months: nmiMonthOptions(boardRows.map((r) => r.bill), lastCompleted), site: siteOptions(sites) }),
    [boardRows, sites, lastCompleted],
  );
  const grid = useMemo(
    () => buildMissingGrid({
      sites: filterSites(sites, filters),
      bills: boardRows.map(gridBillOf),
      tags,
      months: range.months,
    }),
    [sites, filters, boardRows, tags, range.months],
  );
  const shownRows = useMemo(() => visibleRows(grid.rows, onlyGaps), [grid.rows, onlyGaps]);
  // The export is the whole filtered grid, whatever the toggle or page shows.
  const csvRows = useMemo(() => missingCsvRows(grid), [grid]);
  const exportName = range.from === range.to ? `missing-bills-${range.from}` : `missing-bills-${range.from}_${range.to}`;
  const exportCsv = () => downloadCsv(toCsv(MISSING_CSV_HEADERS, csvRows), exportName);
  const exportXlsx = () => exportMultiSheetXLSX([{ name: 'Missing bills', rows: csvRows }], exportName);

  // Back to the first page when what is listed changes.
  const listKey = `${filters.q}|${filters.entity}|${filters.retailer}|${filters.distributor}|${filters.state}|${filters.tariff}|${range.from}|${range.to}|${onlyGaps}`;
  useEffect(() => { setPage(1); }, [listKey]);

  const lastPage = Math.max(1, Math.ceil(shownRows.length / PAGE_SIZE));
  const safePage = Math.min(page, lastPage);
  const pageRows = shownRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Only rows still listed count as selected; a filter change drops the rest.
  const selectedShown = useMemo(
    () => new Set(shownRows.filter((r) => selected.has(r.site.id)).map((r) => r.site.id)),
    [shownRows, selected],
  );
  const allSelected = shownRows.length > 0 && selectedShown.size === shownRows.length;

  const toggleRow = (siteId: string, on: boolean) => {
    setPending(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(siteId); else next.delete(siteId);
      return next;
    });
  };
  const toggleAll = (on: boolean) => {
    setPending(null);
    setSelected(on ? new Set(shownRows.map((r) => r.site.id)) : new Set());
  };

  const applyBulk = () => {
    setBulkError(null);
    // Grid (display) order, so the confirm and the write follow what is on screen.
    const { cells, overwrites } = bulkTagTargets(shownRows, selectedShown, bulkTag);
    if (cells.length === 0) {
      setPending(null);
      setBulkError('The selected NMIs have no missing months to tag.');
      return;
    }
    setPending({ cells, overwrites, tag: bulkTag });
  };

  const confirmBulk = async () => {
    if (!pending) return;
    setBulkSaving(true);
    setBulkError(null);
    try {
      const res = await missingBillService.setTag(pending.cells, pending.tag, bulkNote.trim() || null);
      if (!res.success) {
        // Calls go in chunks and stop at the first failure; earlier chunks are
        // saved, so show the error, drop the stale confirm and reload the tags.
        setBulkError(res.error ?? 'Could not save the tags');
        setPending(null);
        reloadTags();
        return;
      }
      setPending(null);
      setSelected(new Set());
      setBulkNote('');
      setEditing(null);
      reloadTags();
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'Could not save the tags');
    } finally {
      setBulkSaving(false);
    }
  };

  const initialLoad = (loading && sites.length === 0) || (boardLoading && boardRows.length === 0);
  const failure = boardError ?? error;

  return (
    <div data-testid="nmi-view" className="space-y-4">
      <BillFilterBar
        filters={filters}
        onChange={onFilters}
        onClear={() => onFilters(CLEAR_VISIBLE_FILTERS)}
        options={options}
        testIdPrefix="bill-runs"
        searchPlaceholder="Search NMI or client…"
        hide={HIDDEN_FILTERS}
        monthAllText={{ from: `From: ${monthLabel(range.from)}`, to: `To: ${monthLabel(range.to)}` }}
      />

      <div className="flex flex-wrap items-center gap-4">
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            data-testid="nmi-only-gaps"
            type="checkbox"
            checked={onlyGaps}
            onChange={(e) => setOnlyGaps(e.target.checked)}
          />
          Only NMIs with a missing month
        </label>
        <Button
          data-testid="nmi-export"
          variant="outline"
          size="sm"
          disabled={csvRows.length === 0}
          title="Every missing month in the filtered list, not just this page"
          onClick={exportCsv}
        >
          <Download className="h-4 w-4 mr-1" /> Export CSV
        </Button>
        <Button
          data-testid="nmi-export-xlsx"
          variant="outline"
          size="sm"
          disabled={csvRows.length === 0}
          title="Every missing month in the filtered list, not just this page"
          onClick={exportXlsx}
        >
          <Download className="h-4 w-4 mr-1" /> Export XLSX <NewBadge className="ml-1" />
        </Button>
      </div>
      {tagsError && !failure && (
        <p data-testid="nmi-tags-error" className="text-xs text-red-600">
          {`Tags could not be loaded (${tagsError}); the grid is shown without them.`}
        </p>
      )}
      {range.clamped && (
        <p data-testid="nmi-range-clamped" className="text-xs text-amber-700">Showing the last 24 months of the range.</p>
      )}

      {unmatched !== null && unmatched > 0 && (
        <Link
          data-testid="nmi-unmatched"
          to="/bill-validation/inbox?status=unmatched"
          className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 hover:bg-amber-100"
        >
          <Inbox className="h-4 w-4" />
          {`${unmatched} unmatched invoice${unmatched === 1 ? '' : 's'} in the Inbox — check before chasing`}
        </Link>
      )}

      {initialLoad ? (
        <div data-testid="nmi-loading" className="text-center py-16 text-gray-400">Loading missing bills…</div>
      ) : failure ? (
        <div data-testid="nmi-error" className="flex flex-col items-center gap-2 py-16 text-red-500">
          <AlertCircle className="h-5 w-5" />
          <p className="text-sm">{failure}</p>
        </div>
      ) : (
        <>
          <ul data-testid="nmi-summary" className="space-y-0.5 text-xs text-gray-600">
            {grid.summary.map((m) => (
              <li key={m.month} data-testid="nmi-summary-month" data-month={m.month}>
                {`${monthLabel(m.month)}: ${m.expected} expected · ${m.billed} with a bill · ${m.missing} missing`}
                {m.notExpected > 0 && ` · ${m.notExpected} not expected`}
              </li>
            ))}
          </ul>

          {shownRows.length === 0 ? (
            <div data-testid="nmi-empty" className="text-center py-16 text-gray-400">
              {grid.rows.length === 0 ? 'No enrolled sites for this period.' : 'Every enrolled NMI has a bill for this period.'}
            </div>
          ) : (
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              <div data-testid="nmi-bulk-bar" className="flex flex-wrap items-center gap-2 border-b bg-gray-50 px-3 py-2 text-xs text-gray-700">
                <span data-testid="nmi-selected-count">
                  {`${selectedShown.size} selected`}
                  {selectedShown.size > pageRows.filter((r) => selectedShown.has(r.site.id)).length && ' (all pages)'}
                </span>
                <select
                  data-testid="nmi-bulk-tag"
                  className="rounded border border-gray-300 bg-white px-1.5 py-1"
                  value={bulkTag}
                  disabled={busy}
                  onChange={(e) => { setBulkTag(e.target.value as MissingTag); setPending(null); }}
                >
                  {MISSING_TAGS.map((t) => <option key={t} value={t}>{MISSING_TAG_LABELS[t]}</option>)}
                </select>
                <input
                  data-testid="nmi-bulk-note"
                  className="w-48 rounded border border-gray-300 px-1.5 py-1"
                  placeholder="Note (blank keeps existing)"
                  value={bulkNote}
                  disabled={busy}
                  onChange={(e) => setBulkNote(e.target.value)}
                />
                <Button
                  data-testid="nmi-bulk-apply"
                  size="sm"
                  disabled={selectedShown.size === 0 || busy}
                  onClick={applyBulk}
                >
                  Apply to selected
                </Button>
                {pending && (
                  <span data-testid="nmi-bulk-confirm" className="inline-flex items-center gap-2 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-amber-900">
                    <span>
                      {`Tag ${pending.cells.length} missing month${plural(pending.cells.length)} as ${MISSING_TAG_LABELS[pending.tag]}?`}
                      {pending.overwrites > 0 && ` ${pending.overwrites} existing tag${plural(pending.overwrites)} will change.`}
                    </span>
                    <Button data-testid="nmi-bulk-confirm-yes" size="sm" disabled={busy} onClick={() => void confirmBulk()}>
                      {bulkSaving ? 'Saving…' : 'Yes'}
                    </Button>
                    <Button data-testid="nmi-bulk-confirm-no" variant="outline" size="sm" disabled={busy} onClick={() => setPending(null)}>
                      No
                    </Button>
                  </span>
                )}
                {bulkError && <span data-testid="nmi-bulk-error" className="text-red-600">{bulkError}</span>}
              </div>
              <div className="overflow-x-auto">
                <table data-testid="nmi-table" className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b bg-gray-50">
                      <th className="sticky left-0 z-10 bg-gray-50 px-3 py-2 font-medium">
                        <label className="inline-flex items-center gap-2">
                          <input
                            data-testid="nmi-select-all"
                            type="checkbox"
                            aria-label="Select all listed NMIs (all pages)"
                            title="Selects every NMI in the list, across all pages"
                            checked={allSelected}
                            disabled={busy}
                            onChange={(e) => toggleAll(e.target.checked)}
                          />
                          NMI
                        </label>
                      </th>
                      <th className="px-3 py-2 font-medium">Client · Retailer</th>
                      {grid.months.map((m) => (
                        <th key={m} scope="col" className="px-3 py-2 font-medium whitespace-nowrap">{monthLabel(m)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {pageRows.map((row) => (
                      <tr key={row.site.id} data-testid="nmi-row" data-site-id={row.site.id} className="align-top">
                        <td className="sticky left-0 z-10 bg-white px-3 py-2.5">
                          <div className="flex items-start gap-2">
                            <input
                              data-testid="nmi-row-select"
                              type="checkbox"
                              className="mt-0.5"
                              aria-label={`Select ${row.nmi || row.siteLabel}`}
                              checked={selectedShown.has(row.site.id)}
                              disabled={busy}
                              onChange={(e) => toggleRow(row.site.id, e.target.checked)}
                            />
                            <div>
                              <Link
                                data-testid="nmi-row-nmi"
                                to={`/bill-validation/sites/${row.site.id}`}
                                className="block font-mono text-xs text-gray-900 hover:underline"
                              >
                                {row.nmi || '—'}
                              </Link>
                              <p className="text-xs text-gray-500">{row.siteLabel}</p>
                            </div>
                          </div>
                          {row.undatedBills > 0 && (row.nmi ? (
                            <Link
                              data-testid="nmi-undated"
                              to={`/bill-validation/bills?q=${encodeURIComponent(row.nmi)}`}
                              className="block text-[11px] text-amber-700 hover:underline"
                            >
                              {`${row.undatedBills} bill${plural(row.undatedBills)} without a period`}
                            </Link>
                          ) : (
                            // No NMI to search the Bills list by, so no link.
                            <span data-testid="nmi-undated" className="block text-[11px] text-amber-700">
                              {`${row.undatedBills} bill${plural(row.undatedBills)} without a period`}
                            </span>
                          ))}
                        </td>
                        <td className="px-3 py-2.5 text-xs">
                          <p data-testid="nmi-row-client" className="font-medium text-gray-900">{row.client || '—'}</p>
                          <p data-testid="nmi-row-retailer" className="text-gray-500">{row.retailer || '—'}</p>
                        </td>
                        {row.cells.map((cell) => {
                          const tag = cell.kind === 'no_bill' || cell.kind === 'missing_invoice' ? cell.tag : null;
                          const notExpected = tag?.tag === 'not_expected';
                          const taggable = cell.kind === 'no_bill' || cell.kind === 'missing_invoice';
                          const isEditing = taggable && editing?.siteId === row.site.id && editing.month === cell.month;
                          return (
                            <td
                              key={cell.month}
                              data-testid="nmi-cell"
                              data-month={cell.month}
                              data-kind={cell.kind}
                              data-tag={tag?.tag ?? ''}
                              className={cn(
                                'px-3 py-2.5 text-xs whitespace-nowrap',
                                (cell.kind === 'not_enrolled' || notExpected) && 'bg-gray-50 text-gray-400',
                                taggable && !busy && 'cursor-pointer hover:bg-blue-50/40',
                              )}
                              onClick={taggable && !busy ? (e) => {
                                // Links, buttons and the open editor handle their own clicks.
                                if ((e.target as HTMLElement).closest('a, button, [data-testid="nmi-tag-editor"]')) return;
                                setEditing(isEditing ? null : { siteId: row.site.id, month: cell.month });
                              } : undefined}
                            >
                              <CellBody cell={cell} notExpected={notExpected} />
                              {tag && (
                                <span className="ml-1.5 inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600">
                                  {MISSING_TAG_LABELS[tag.tag]}
                                </span>
                              )}
                              {taggable && (
                                <button
                                  ref={(el) => {
                                    const key = cellKey(row.site.id, cell.month);
                                    if (el) tagButtons.current.set(key, el); else tagButtons.current.delete(key);
                                  }}
                                  type="button"
                                  data-testid="nmi-cell-tag"
                                  aria-label={`Tag ${row.nmi || row.siteLabel} ${monthLabel(cell.month)}`}
                                  aria-expanded={isEditing}
                                  disabled={busy}
                                  className="ml-1.5 rounded border border-gray-300 bg-white px-1.5 py-0.5 text-[10px] font-medium text-gray-600 hover:border-blue-400 hover:text-blue-700 disabled:opacity-50"
                                  onClick={() => setEditing(isEditing ? null : { siteId: row.site.id, month: cell.month })}
                                >
                                  Tag
                                </button>
                              )}
                              {isEditing && (
                                <MissingTagEditor
                                  key={`${row.site.id}|${cell.month}|${tag?.id ?? ''}`}
                                  cell={{ siteId: row.site.id, month: cell.month }}
                                  current={tag}
                                  disabled={bulkSaving}
                                  onSavingChange={setEditorSaving}
                                  onDone={(changed) => {
                                    setEditing(null);
                                    // Cancel, Save, Clear or Escape: focus goes back to this cell's Tag button.
                                    setFocusReturn(cellKey(row.site.id, cell.month));
                                    if (changed) reloadTags();
                                  }}
                                />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between px-4 py-2.5 border-t text-xs text-gray-500">
                <span data-testid="nmi-page-info">
                  {shownRows.length} NMI{shownRows.length === 1 ? '' : 's'} · page {safePage} of {lastPage}
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>
                    <ChevronLeft className="h-4 w-4" /> Prev
                  </Button>
                  <Button variant="outline" size="sm" disabled={safePage >= lastPage} onClick={() => setPage(safePage + 1)}>
                    Next <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
